import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="image-upload" (textarea または contenteditable の本文に付ける)
export default class extends Controller {
  connect() {
    this.pendingDrops = 0
    this.form = this.element.closest("form")
    this.trackPending(0) // 応答待ち中に離脱した画面を Turbo が履歴から復元しても、保存ボタンを無効のまま残さない

    // グローバルにドラッグイベントを拾う
    this.onDragOver = this.handleDragOver.bind(this)
    this.onDrop     = this.handleDrop.bind(this)

    window.addEventListener("dragover", this.onDragOver)
    window.addEventListener("drop",     this.onDrop)
  }

  disconnect() {
    // コントローラが外れたらクリーンアップ
    window.removeEventListener("dragover", this.onDragOver)
    window.removeEventListener("drop",     this.onDrop)
  }

  // ---- Drag & Drop ------------------------------------------------------

  handleDragOver(event) {
    // ファイル以外は無視
    if (!event.dataTransfer.types.includes("Files")) return

    event.preventDefault()                 // ★ 既定動作を止める
    event.dataTransfer.dropEffect = "copy" // カーソル表示だけ維持
  }

  async handleDrop(event) {
    const files = Array.from(event.dataTransfer.files)
    if (!files.length) return

    event.preventDefault() // ★ ここも必須。既定動作＝画像貼り付けを防ぐ

    const input = this.element
    const replacePlaceholder = this.insertPlaceholder(input, event)
    this.notifyChange(input)

    this.trackPending(+1)
    const urls = []
    const failures = []
    for (const file of files) {
      if (!input.isConnected) break
      try {
        urls.push(await this.upload(file))
      } catch (error) {
        const message = error instanceof TypeError ? "通信に失敗しました。ログイン状態を確認して、もう一度お試しください。" : error.message
        failures.push(`「${file.name}」をアップロードできませんでした。\n${message}`)
      }
    }
    this.trackPending(-1)

    replacePlaceholder(urls.length ? `${urls.map(url => `[[${url}]]`).join(" ")}\n` : "")
    this.notifyChange(input)
    if (failures.length && input.isConnected) alert(failures.join("\n\n"))
  }

  // 応答待ちのまま保存すると、成功したファイルの記法が本文に入らないまま保存されるので、待っている間は保存させない
  trackPending(delta) {
    this.pendingDrops += delta
    for (const button of this.form?.querySelectorAll("button[type=submit], input[type=submit]") ?? []) {
      button.disabled = this.pendingDrops > 0
    }
  }

  // textarea-resize / block-editor#serialize / confirm-leave に本文の変更を知らせる
  notifyChange(input) {
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  insertPlaceholder(input, event) {
    const placeholder = `<!-- upload-${Date.now()} アップロード中 -->\n`

    if (input.isContentEditable) {
      // Chrome は focus() で編集面の先頭までスクロールし、ドロップ座標が指す位置がずれる
      input.focus({ preventScroll: true })
      const node = document.createTextNode(placeholder)
      this.dropRange(input, event).insertNode(node)
      window.getSelection().collapse(node, node.length)
      return text => {
        node.nodeValue = node.nodeValue.replace(placeholder, text)
        if (!node.nodeValue) node.remove()
      }
    }

    input.focus() // どこに落としても本文にフォーカス
    this.insertTextAtCursor(input, input.selectionStart, placeholder)
    return text => { input.value = input.value.replace(placeholder, text) }
  }

  async upload(file) {
    const formData = new FormData()
    formData.append("image", file)

    const response = await post("/files", { body: formData, responseKind: "json" })
    if (!response.ok) throw new Error(await this.errorMessage(response))

    return (await response.json).url
  }

  async errorMessage(response) {
    if (response.redirected) return "ログインし直してから、もう一度お試しください。"
    if (response.contentType === "application/json") {
      const data = await response.json
      if (data.errors) return data.errors.join("\n")
    }
    if (response.statusCode === 422) return "ページを再読み込みしてから、もう一度お試しください。"
    return `HTTP ${response.statusCode}`
  }

  // ---- 挿入位置のユーティリティ -----------------------------------------

  insertTextAtCursor(textarea, position, text) {
    const value = textarea.value
    textarea.value = value.slice(0, position) + text + value.slice(position)
    textarea.selectionStart = textarea.selectionEnd = position + text.length
  }

  // contenteditable は dragover 中に selection が動かない (drag caret は別描画) ので座標から位置を取る。
  // 編集面の外 (隣の textarea 等) や編集不可の領域 (contenteditable=false) に落としたときは編集面の末尾。
  // Text ノードの端は前後に置く (insertNode が端で split して空の Text ノードを残さないように)
  dropRange(surface, event) {
    const caret = this.caretFromPoint(event.clientX, event.clientY)
    const range = document.createRange()
    if (!caret || !surface.contains(caret.node) || !this.elementOf(caret.node).isContentEditable) {
      range.selectNodeContents(surface)
      range.collapse(false)
    } else if (caret.node.nodeType === Node.TEXT_NODE && caret.offset === 0) {
      range.setStartBefore(caret.node)
    } else if (caret.node.nodeType === Node.TEXT_NODE && caret.offset === caret.node.length) {
      range.setStartAfter(caret.node)
    } else {
      range.setStart(caret.node, caret.offset)
    }
    return range
  }

  caretFromPoint(x, y) {
    if (!document.caretPositionFromPoint) {
      const range = document.caretRangeFromPoint(x, y)
      return range && { node: range.startContainer, offset: range.startOffset }
    }

    const position = document.caretPositionFromPoint(x, y)
    return position && { node: position.offsetNode, offset: position.offset }
  }

  elementOf(node) {
    return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
  }
}
